import { useState, useEffect, useRef } from "react";
import { X, Upload, Building, MapPin, Package, User, Image as ImageIcon, Camera } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { useAuth } from "../../../../context/AuthContext";
import Swal from "sweetalert2";
import { insertarEncomienda } from "../../../../api/encomiendaService";
import { obtenerTorres, Torre } from "../../../../api/torreService";
import { obtenerApartamentos, Apartamento } from "../../../../api/apartamentoService";
import { obtenerTiposEncomienda, TipoEncomienda } from "../../../../api/tipoEncomiendaService";

interface CreateEncomiendaModalProps {
  isOpen: boolean;
  onClose: (created: boolean) => void;
}

export default function CreateEncomiendaModal({ isOpen, onClose }: CreateEncomiendaModalProps) {
  const { session } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form data
  const [selectedTorre, setSelectedTorre] = useState("");
  const [selectedApartamento, setSelectedApartamento] = useState("");
  const [selectedTipo, setSelectedTipo] = useState("");
  const [nombreMensajero, setNombreMensajero] = useState("");
  const [fotoBase64, setFotoBase64] = useState("");
  const [previewImage, setPreviewImage] = useState("");
  
  // Estados para cámara
  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Catálogos
  const [torres, setTorres] = useState<Torre[]>([]);
  const [apartamentos, setApartamentos] = useState<Apartamento[]>([]);
  const [tiposEncomienda, setTiposEncomienda] = useState<TipoEncomienda[]>([]);
  
  const [isLoadingTorres, setIsLoadingTorres] = useState(false);
  const [isLoadingApartamentos, setIsLoadingApartamentos] = useState(false);
  const [isLoadingTipos, setIsLoadingTipos] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadCatalogos();
    }
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const loadCatalogos = async () => {
    try {
      const unidadCodigo = session?.unidad?.id || "";

      setIsLoadingTorres(true);
      setIsLoadingTipos(true);

      const torresResponse = await obtenerTorres({
        "unidad residencial codigo": unidadCodigo,
      });
      
      if (torresResponse.code === "1" && torresResponse.object_) {
        setTorres(torresResponse.object_);
      }
      setIsLoadingTorres(false);

      const tiposResponse = await obtenerTiposEncomienda();
      if (tiposResponse.code === "1" && tiposResponse.object_) {
        setTiposEncomienda(tiposResponse.object_);
      }
      setIsLoadingTipos(false);
    } catch (error) {
      console.error("Error al cargar catálogos:", error);
      setIsLoadingTorres(false);
      setIsLoadingTipos(false);
    }
  };

  const loadApartamentos = async (torreCodigo: string) => {
    try {
      setIsLoadingApartamentos(true);
      const unidadCodigo = session?.unidad?.id || "";
      const response = await obtenerApartamentos({
        "unidad residencial codigo": unidadCodigo,
        "torre codigo": torreCodigo,
      });

      if (response.code === "1" && response.object_) {
        setApartamentos(response.object_);
      }
    } catch (error) {
      console.error("Error al cargar apartamentos:", error);
      setApartamentos([]);
    } finally {
      setIsLoadingApartamentos(false);
    }
  };

  const handleTorreChange = (value: string) => {
    setSelectedTorre(value);
    setSelectedApartamento("");
    setApartamentos([]);
    if (value) {
      loadApartamentos(value);
    }
  };

  const startCamera = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error("La API de cámara no está disponible en este navegador.");
      }

      // Mostramos primero el modal para asegurarnos de que el <video> exista
      setShowCamera(true);

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: false,
      });

      setStream(mediaStream);
    } catch (error) {
      console.error("Error al acceder a la cámara:", error);
      setShowCamera(false);
      Swal.fire({
        icon: "error",
        title: "Error de cámara",
        text:
          "No se pudo acceder a la cámara. Verifica los permisos del navegador y que la cámara no esté siendo usada por otra aplicación.",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
    }
  };

  // Cuando el modal está visible y tenemos un stream, lo conectamos al <video>
  useEffect(() => {
    if (showCamera && stream && videoRef.current) {
      videoRef.current.srcObject = stream;

      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch((err) => {
          console.error("No se pudo reproducir el video de la cámara:", err);
        });
      }
    }
  }, [showCamera, stream]);

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setShowCamera(false);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      if (context) {
        context.drawImage(video, 0, 0);
        const base64String = canvas.toDataURL('image/jpeg', 0.8);
        setFotoBase64(base64String);
        setPreviewImage(base64String);
        stopCamera();
      }
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Swal.fire({
          icon: "warning",
          title: "Archivo muy grande",
          text: "La imagen no debe superar los 5MB",
          confirmButtonColor: "#3d5a40",
          didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
        });
        return;
      }

      if (!file.type.startsWith("image/")) {
        Swal.fire({
          icon: "warning",
          title: "Formato inválido",
          text: "Por favor seleccione una imagen válida",
          confirmButtonColor: "#3d5a40",
          didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
        });
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setFotoBase64(base64String);
        setPreviewImage(base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedTorre) {
      Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Por favor seleccione una torre",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
      return;
    }

    if (!selectedApartamento) {
      Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Por favor seleccione un apartamento",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
      return;
    }

    if (!selectedTipo) {
      Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Por favor seleccione el tipo de encomienda",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
      return;
    }

    if (!fotoBase64) {
      Swal.fire({
        icon: "warning",
        title: "Foto requerida",
        text: "Por favor suba una foto de la encomienda",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const usuarioEmail = session?.email || session?.usuario?.email || "";
      
      if (!usuarioEmail) {
        Swal.fire({
          icon: "error",
          title: "Error de sesión",
          text: "No se pudo obtener el correo del usuario. Por favor, inicie sesión nuevamente.",
          confirmButtonColor: "#d33",
          didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
        });
        return;
      }

      const payload = {
        recibido_por_codigo: usuarioEmail,
        nombre_mensajero: nombreMensajero.trim(),
        apartamento_codigo: selectedApartamento,
        tipo_codigo: selectedTipo,
        foto_base64: fotoBase64,
      };

      const response = await insertarEncomienda(payload);

      if (response.code === "1" || response.code === 1) {
        Swal.fire({
          icon: "success",
          title: "¡Encomienda registrada!",
          text: response.message || "La encomienda se ha registrado exitosamente",
          confirmButtonColor: "#3d5a40",
          timer: 2000,
          timerProgressBar: true,
          didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
        });
        onClose(true);
      } else {
        throw new Error(response.message || "Error al registrar la encomienda");
      }
    } catch (error) {
      console.error("Error al crear encomienda:", error);
      const errorMessage = error instanceof Error ? error.message : "Error al registrar la encomienda";
      const mensajeTraducido = errorMessage.includes("Request failed") 
        ? "Error al registrar la encomienda. Por favor, intente nuevamente."
        : errorMessage.includes("Network Error")
        ? "Error de conexión. Verifique su conexión a internet."
        : errorMessage;
      
      Swal.fire({
        icon: "error",
        title: "Error",
        text: mensajeTraducido,
        confirmButtonColor: "#d33",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isSubmitting) {
          onClose(false);
        }
      }}
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[95vh] flex flex-col">
        <div className="sticky top-0 bg-gradient-to-r from-military-500 to-military-600 text-white px-6 py-4 rounded-t-xl flex items-center justify-between z-10">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Package size={28} />
            Nueva Encomienda
          </h2>
          <button
            onClick={() => onClose(false)}
            className="text-white hover:bg-white/20 rounded-full p-1 transition"
            disabled={isSubmitting}
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
          <div className="bg-gray-50 rounded-lg p-4">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              <ImageIcon size={18} className="inline mr-2 text-military-600" />
              Foto de la Encomienda *
            </label>
            
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-military-400 hover:bg-gray-50 transition-all">
              {previewImage ? (
                <div className="relative inline-block mb-4 group">
                  <img
                    src={previewImage}
                    alt="Preview"
                    className="max-h-72 max-w-full rounded-xl shadow-lg object-contain border-2 border-gray-200"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setPreviewImage("");
                      setFotoBase64("");
                      if (fileInputRef.current) {
                        fileInputRef.current.value = "";
                      }
                    }}
                    className="absolute -top-3 -right-3 bg-red-500 text-white p-2.5 rounded-full hover:bg-red-600 transition-all shadow-xl hover:scale-110 z-10"
                    title="Eliminar imagen"
                  >
                    <X size={18} />
                  </button>
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 rounded-xl transition-all flex items-center justify-center">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                      id="file-upload"
                      disabled={isSubmitting}
                    />
                    <label
                      htmlFor="file-upload"
                      className={`absolute inset-0 flex items-center justify-center cursor-pointer ${
                        isSubmitting ? "cursor-not-allowed" : ""
                      }`}
                    >
                      <span className="bg-white/95 backdrop-blur-sm text-military-600 px-4 py-2 rounded-lg shadow-lg border border-military-200 font-medium text-sm opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-2">
                        <ImageIcon size={16} />
                        Cambiar imagen
                      </span>
                    </label>
                  </div>
                </div>
              ) : (
                <>
                  <div className="py-8">
                    <div className="bg-military-100 rounded-full p-4 w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                      <Upload size={32} className="text-military-600" />
                    </div>
                    <p className="text-gray-700 font-medium mb-1">
                      Click para subir una imagen
                    </p>
                    <p className="text-xs text-gray-500">
                      PNG, JPG o GIF (máx. 5MB)
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center mt-4">
                    <Button
                      type="button"
                      onClick={startCamera}
                      variant="outline"
                      className="flex items-center justify-center gap-2 border-2 border-military-500 text-military-600 hover:bg-military-50"
                      disabled={isSubmitting}
                    >
                      <Camera size={18} />
                      Tomar Foto
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                      id="file-upload"
                      disabled={isSubmitting}
                    />
                    <label
                      htmlFor="file-upload"
                      className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 border-2 border-military-500 text-military-600 rounded-lg cursor-pointer hover:bg-military-50 transition-all font-medium ${
                        isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    >
                      <Upload size={18} />
                      Seleccionar imagen
                    </label>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <Building size={16} className="inline mr-2 text-military-600" />
              Torre *
            </label>
            <select
              value={selectedTorre}
              onChange={(e) => handleTorreChange(e.target.value)}
              disabled={isSubmitting || isLoadingTorres}
              className="w-full rounded-lg border-gray-300 focus:border-military-500 focus:ring-military-500 p-3 bg-white shadow-sm"
              required
            >
              {isLoadingTorres ? (
                <option value="">Cargando torres...</option>
              ) : (
                <>
                  <option value="">Seleccionar...</option>
                  {torres.map((torre) => (
                    <option key={torre.tor_codigo} value={torre.tor_codigo}>
                      {torre.tor_nombre}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <MapPin size={16} className="inline mr-2 text-military-600" />
              Apartamento *
            </label>
            <select
              value={selectedApartamento}
              onChange={(e) => setSelectedApartamento(e.target.value)}
              disabled={!selectedTorre || isSubmitting || isLoadingApartamentos}
              className="w-full rounded-lg border-gray-300 focus:border-military-500 focus:ring-military-500 p-3 bg-white shadow-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
              required
            >
              {isLoadingApartamentos ? (
                <option value="">Cargando apartamentos...</option>
              ) : (
                <>
                  <option value="">Seleccionar...</option>
                  {apartamentos.map((apt) => (
                    <option key={apt.apt_codigo} value={apt.apt_codigo}>
                      {apt.apt_nombre}
                    </option>
                  ))}
                </>
              )}
            </select>
            {!selectedTorre && !isLoadingApartamentos && (
              <p className="text-xs text-gray-500 mt-1">
                Primero seleccione una torre
              </p>
            )}
            {isLoadingApartamentos && (
              <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                <div className="h-3 w-3 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                Cargando...
              </p>
            )}
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <Package size={16} className="inline mr-2 text-military-600" />
              Tipo de Encomienda *
            </label>
            <select
              value={selectedTipo}
              onChange={(e) => setSelectedTipo(e.target.value)}
              disabled={isSubmitting || isLoadingTipos}
              className="w-full rounded-lg border-gray-300 focus:border-military-500 focus:ring-military-500 p-3 bg-white shadow-sm"
              required
            >
              {isLoadingTipos ? (
                <option value="">Cargando tipos...</option>
              ) : (
                <>
                  <option value="">Seleccionar...</option>
                  {tiposEncomienda.map((tipo) => (
                    <option key={tipo.ten_codigo} value={tipo.ten_codigo}>
                      {tipo.ten_nombre}
                    </option>
                  ))}
                </>
              )}
            </select>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <User size={16} className="inline mr-2 text-military-600" />
              Nombre del Mensajero (Opcional)
            </label>
            <Input
              type="text"
              value={nombreMensajero}
              onChange={(e) => setNombreMensajero(e.target.value)}
              placeholder="Ej: Juan Pérez - Servientrega"
              disabled={isSubmitting}
              className="w-full p-3 shadow-sm"
            />
          </div>

          <div className="sticky bottom-0 bg-white border-t pt-4 mt-6 -mx-6 px-6 pb-6">
            <div className="flex gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onClose(false)}
                disabled={isSubmitting}
                className="flex-1 border-2 hover:bg-gray-50"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-military-500 hover:bg-military-600 text-white shadow-lg font-semibold"
              >
                {isSubmitting ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Package size={20} className="mr-2" />
                    Registrar Encomienda
                  </>
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>

      {/* Modal de cámara */}
      {showCamera && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[10000] p-4">
          <div className="bg-white rounded-lg p-4 sm:p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg sm:text-xl font-bold text-military-600">
                Tomar Foto
              </h3>
              <button
                onClick={stopCamera}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="relative bg-black rounded-lg overflow-hidden">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  className="w-full h-auto max-h-[60vh]"
                />
                <canvas ref={canvasRef} className="hidden" />
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={capturePhoto}
                  className="bg-military-500 hover:bg-military-600 text-white"
                >
                  <Camera className="h-4 w-4 mr-2" />
                  Capturar Foto
                </Button>
                <Button
                  onClick={stopCamera}
                  variant="outline"
                >
                  Cancelar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

